import { configs } from '../configs';
import authProvider from '../authProvider';
import { Promotion, PromotionStatus } from 'public-contracts';

const apiCall = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const auth = authProvider.auth;
  const accessToken = await auth.getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };

  const response = await fetch(`${configs.apiRoot}/promotions${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      error.message || `Failed to fetch promotions: ${response.statusText}`
    );
  }

  return response.json();
};

export default class PromotionsService {
  async getPromotions(params?: {
    page?: number;
    perPage?: number;
    filter?: any;
    sort?: any;
  }): Promise<Promotion[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.perPage)
        queryParams.append('size', params.perPage.toString());
      if (params?.filter)
        queryParams.append('filter', JSON.stringify(params.filter));
      if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

      const endpoint = queryParams.toString()
        ? `?${queryParams.toString()}`
        : '';
      return await apiCall<Promotion[]>(endpoint);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      throw error;
    }
  }

  async getActivePromotions(orderType?: string): Promise<Promotion[]> {
    try {
      // Use the less restrictive /active endpoint
      const queryParams = new URLSearchParams();
      if (orderType) {
        queryParams.append('orderType', orderType);
      }
      const endpoint = queryParams.toString()
        ? `/active?${queryParams.toString()}`
        : '/active';

      const result = await apiCall<Promotion[] | { data: Promotion[] }>(
        endpoint
      );
      // Handle both array response and { data: [] } response format
      if (Array.isArray(result)) {
        return result;
      } else if (result && typeof result === 'object' && 'data' in result) {
        return Array.isArray((result as { data: Promotion[] }).data)
          ? (result as { data: Promotion[] }).data
          : [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching active promotions:', error);
      throw error;
    }
  }

  async getPromotion(id: string): Promise<Promotion> {
    try {
      return await apiCall<Promotion>(`/${id}`);
    } catch (error) {
      console.error('Error fetching promotion:', error);
      throw error;
    }
  }
}

export const promotionsService = new PromotionsService();
